import { collabStateManager } from '@databases/collab-state-manager';
import { isAIFCStagingAreaView } from '@databases/database-state';
import { isAnyNativeEmbed } from '@databases/modes';
import usePanel from '@databases/panel/usePanel';
import usePanelState from '@databases/panel/usePanelState';
import { storage as createWithTemplateStorage } from '@databases/templates/storage';
import { useEffect } from 'react';

import { getExperimentValue, isFeatureFlagEnabled } from '@canvas/client-feature-flags';
import { getFrameApi } from '@canvas/frame-api/host-api/host-frame-context';

/**
 * This particular hook being so high up in the application causes every child to re-render
 * when the panel state changes. This should be moved.
 *
 * @deprecated This will become an event listener on panel state changes.
 */
export const useSetupPanel = ({ contentId }: { contentId: string }) => {
  const { showPanel, hidePanel } = usePanel();
  const { isPanelVisible } = usePanelState();
  const collabManager = collabStateManager.getCollabManager(contentId);
  const wasCreatedThisSession = collabManager?.wasCreatedThisSession;
  const isInAIFCStagingAreaView = isAIFCStagingAreaView();
  const frameApiHref = getFrameApi().href;
  const frameApiHrefUrl = new URL(frameApiHref);
  const hasAiCreateFlowQueryParam =
    frameApiHrefUrl.searchParams.get('flow') === 'aiCreate';

  useEffect(() => {
    if (hasAiCreateFlowQueryParam) {
      return; // Do not render setup panel if created through AI flow
    }

    if (isInAIFCStagingAreaView) {
      if (isPanelVisible('DatabaseSetupPanel')) {
        hidePanel();
      }
      return; // Do not render setup panel in rovo AIFC staging area
    }

    if (isAnyNativeEmbed()) {
      if (isPanelVisible('DatabaseSetupPanel')) {
        hidePanel();
      }
      return; // Do not render setup panel if created as an embed in whiteboards
    }

    // When navigating between databases in content tree we hide the panel unless it was created in the current session
    if (isPanelVisible('DatabaseSetupPanel')) {
      hidePanel();
    }

    // When templates are applied from confluence quick insert
    if (
      createWithTemplateStorage.exists(contentId) &&
      isFeatureFlagEnabled('applyDatabaseTemplateFromQuickInsert')
    ) {
      createWithTemplateStorage.remove();
      return;
    }

    const isCwrBlankObjectExperimentEnabled = getExperimentValue(
      'cwrCanvasBlankObjectExperiment',
      'isEnabled',
      { fireExperimentExposure: false }
    );

    if (wasCreatedThisSession && !isCwrBlankObjectExperimentEnabled) {
      showPanel('DatabaseSetupPanel');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
